import { useState, useEffect } from "react";
import { 
  Flex, 
  Tabs, 
  Tab,
  TabList, 
  TabPanels, 
  TabPanel,
  Spinner, 
 } from "@chakra-ui/react";
import {
  CrossmintEmbeddedCheckout,
  useCrossmintCheckout
} from '@crossmint/client-sdk-react-ui';
import { IconCreditCard, IconWallet } from "icons";
import { useUserSettings } from "hooks";
import { useCheckout, useWallet } from 'context';
import OrderTotal from "components/OrderTotal/OrderTotal";
import TicketAmount from "components/TicketAmount/TicketAmount";
import CheckoutLoading from "components/CheckoutLoading/CheckoutLoading";

type CryptoTabCommonProps = {
  quantity?: number;
  setQuantity?: (value: number) => void;
};

type CheckoutComponentProps<T> = {
  onSuccessfulPurchase?: (orderIdentifier: string) => void;
  collectionId: string;
  projectId: string;
  mintConfig: Record<string, unknown>;
  environment?: 'staging' | 'production';
  numberInput?: boolean;
  quantity?: number;
  setQuantity?: (value: number) => void;  
  cryptoTabProps?: T & CryptoTabCommonProps;
  CryptoTab?: React.ComponentType<T & CryptoTabCommonProps>;
  orderType?: 'fusion' | 'tuneUp' | 'upgrade' | 'default';
  paymentMethods?: 'card' | 'crypto' | 'fiat';
};

const CheckoutComponent = <T,>({
  onSuccessfulPurchase,
  collectionId,
  projectId,
  mintConfig,
  environment,
  numberInput,
  quantity = 1,
  paymentMethods,
  setQuantity = () => null,
  cryptoTabProps,
  CryptoTab,
  orderType = 'default',
}: CheckoutComponentProps<T>) => {
  const { setIsOperationBlocked } = useWallet();
  const { 
    isLoadingPurchase,
  } = useCheckout();
  const { getUserSettings } = useUserSettings();
  const { data } = getUserSettings;
  const { email, walletAddress } = data?.getUser || {};
  const { order } = useCrossmintCheckout();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  useEffect(() => {
    setIsOperationBlocked(true)
  });


  useEffect(() => {
    if (!order) return;

    switch (order.phase) {
      case 'completed':
        onSuccessfulPurchase && onSuccessfulPurchase(order.orderId);
        break;
    }
  }, [order]);

  // Force a small delay before rendering the CrossmintEmbeddedCheckout component
  const [showCheckout, setShowCheckout] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCheckout(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Flex
      borderRadius="8px"
      w="full"
      gap="4"
      // maxH="fit-content"
      background="linear-gradient(180deg, #121314 0%, #141516 50%, #1D1D1E 100%)"
      border={{ base: 'none', md: '1.5px solid rgba(255, 255, 255, 0.1)' }}
      flexDir={'column'}
      align="center"
      px={{ base: 4, md: 8 }}
      py={{ base: 4, md: 8 }}
      overflowY="auto"
      className="scroll-thin"
    >
      {isLoadingPurchase ? (
        <CheckoutLoading />
      ) : (
        <>
          <Tabs w="full" variant="tabRaces" index={selectedTabIndex} onChange={(index) => setSelectedTabIndex(index)}>
            <TabList>
              <Tab
                gap={2}
                fontSize={{ base: '0.75rem', md: '1rem' }}
                lineHeight={{ base: '1rem', md: '120%' }}
                borderTopRadius="4px"
                fontFamily="heading"
                fontWeight="400"
                w="full"
                px={{ base: 2, md: 4 }}
              >
                <IconCreditCard 
                  __css={{
                    path: {
                      fill: selectedTabIndex === 0 ? 'treasuredTeal.100' : 'white.40'
                    }
                  }}
                  width="1.25rem"
                  height="auto"
                  mb="1px"
                />
                Card
              </Tab>
              {CryptoTab && (
                <Tab
                  gap={2}
                  fontSize={{ base: '0.75rem', md: '1rem' }}
                  lineHeight={{ base: '1rem', md: '120%' }}
                  borderTopRadius="4px"
                  fontFamily="heading"
                  fontWeight="400"
                  w="full"
                  alignItems="center"
                  px={{ base: 2, md: 4 }}
                >
                  <IconWallet 
                    __css={{
                      path: {
                        fill: selectedTabIndex === 1 ? 'treasuredTeal.100' : 'white.40'
                      }
                    }}
                  />
                  Wallet
                </Tab>
              )}
              <Tab
                gap={2}
                fontSize={{ base: '0.75rem', md: '1rem' }}
                lineHeight={{ base: '1rem', md: '120%' }}
                borderTopRadius="4px"
                fontFamily="heading"
                fontWeight="400"
                w="full"
                px={{ base: 2, md: 4 }}
              >
                <IconWallet
                  __css={{
                    path: {
                      fill: selectedTabIndex === 2 ? 'treasuredTeal.100' : 'white.40'
                    }
                  }}
                />
                Ext. Wallet
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel p="0" py="4">
                {!showCheckout ? (
                  <Flex w="full" h="full" justifyContent="center" alignItems="center" py="4">
                    <Spinner />
                  </Flex>
                ) : (
                  <Flex w="full" flexDir="column"  gap="2">
                    <CrossmintEmbeddedCheckout
                      lineItems={{
                        collectionLocator: `crossmint:${collectionId}`,
                        callData: mintConfig
                      }}
                      payment={{
                        crypto: { enabled: false },
                        fiat: { 
                          enabled: true,
                          allowedMethods: {
                            googlePay: false,
                            card: true,
                            applePay: true
                          } 
                        },
                        receiptEmail: email || ''
                      }}
                      recipient={{
                        walletAddress: walletAddress || '',
                      }}
                      locale="en-US"
                      appearance={{
                        fonts: [
                          { cssSrc: 'https://fonts.googleapis.com/css2?family=Chakra+Petch:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&display=swap'},
                          { cssSrc: 'https://fonts.googleapis.com/css2?family=Audiowide&display=swap'}
                        ],
                        variables: {
                          // Typography
                          fontFamily: "Chakra Petch, system-ui, sans-serif",

                          // Spacing
                          spacingUnit: '0.15rem',
                          borderRadius: '8px',

                          // Colors
                          colors: {
                            backgroundPrimary: 'rgba(21, 21, 23, 1)',
                            borderPrimary: 'rgba(255, 255, 255, 0.2)',
                            textPrimary: 'rgba(255, 255, 255, 0.4)',
                            textSecondary: 'rgba(255, 255, 255, 0.8)',
                            accent: '#E2645A',
                            danger: '#E2645A'
                          }
                        },
                        rules: {
                          DestinationInput: {
                            display: 'hidden',
                          },
                          ReceiptEmailInput: {
                            display: 'hidden',
                          },
                          Input: {
                            borderRadius: '4px',
                            colors: {
                              text: 'rgba(255, 255, 255, 0.8)',
                              background: 'rgba(21, 21, 23, 1)',
                              border: 'rgba(255, 255, 255, 0.2)',
                              placeholder: 'rgba(255, 255, 255, 0.4)',
                            },
                            hover: {
                              colors: {
                                  border: "rgba(255, 255, 255, 0.4)",
                              },
                            },
                            focus: {
                              colors: {
                                border: "rgba(255, 255, 255, 0.4)",
                                boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.2)",
                              },
                            },
                          },
                          PrimaryButton: {
                            borderRadius: '4px',
                            font: {
                              family: 'Audiowide, system-ui, sans-serif',
                              size: '16px',
                              weight: '400',
                            },
                            colors: {
                              background: 'rgba(26, 116, 107, 1)',
                              text: 'rgba(255, 255, 255, 0.8)',
                            },
                            hover: {
                              colors: {
                                background: 'rgba(79, 197, 186, 1)',
                                text: 'rgba(255, 255, 255, 1)',
                              },
                            },
                            disabled: {
                              colors: {
                                background: 'rgba(38, 166, 154, 0.5)',
                                text: 'rgba(255, 255, 255, 0.5)',
                              },
                            },
                          }

                        }
                      }}
                    />
                    {/* <OrderTotal total={Number(order?.quote.totalPrice?.amount) || 0} type={orderType} /> */}
                  </Flex>
                )}
              </TabPanel>

              <TabPanel p="0" py="4">
                {CryptoTab && (
                  <CryptoTab
                    quantity={quantity}
                    setQuantity={setQuantity}
                    {...(cryptoTabProps as JSX.IntrinsicAttributes &
                      T &
                      CryptoTabCommonProps)}
                  />
                )}
              </TabPanel>

              <TabPanel p="0" py="4">
                {!showCheckout ? (
                  <Flex w="full" h="full" justifyContent="center" alignItems="center" py="4">
                    <Spinner />
                  </Flex>
                ) : (
                  <Flex w="full" flexDir="column" minH="380px" gap="2">
                    <CrossmintEmbeddedCheckout
                      lineItems={{
                        collectionLocator: `crossmint:${collectionId}`,
                        callData: mintConfig
                      }}
                      payment={{
                        crypto: { 
                          enabled: true,
                          defaultChain: 'polygon',
                          defaultCurrency: 'usdc'
                        },
                        fiat: { 
                          enabled: false,
                          allowedMethods: {
                            googlePay: false,
                            card: false,
                            applePay: false
                          } 
                        },
                        receiptEmail: email || ''
                      }}
                      recipient={{
                        walletAddress: walletAddress || '',
                      }}
                      locale="en-US"
                      appearance={{
                        fonts: [
                          { cssSrc: 'https://fonts.googleapis.com/css2?family=Chakra+Petch:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&display=swap'},
                          { cssSrc: 'https://fonts.googleapis.com/css2?family=Audiowide&display=swap'}
                        ],
                        variables: {
                          // Typography
                          fontFamily: "Chakra Petch, system-ui, sans-serif",

                          // Spacing
                          spacingUnit: '0.15rem',
                          borderRadius: '8px',

                          // Colors
                          colors: {
                            backgroundPrimary: 'rgba(21, 21, 23, 1)',
                            borderPrimary: 'rgba(255, 255, 255, 0.2)',
                            textPrimary: 'rgba(255, 255, 255, 0.4)',
                            textSecondary: 'rgba(255, 255, 255, 0.8)',
                            accent: '#E2645A',
                            danger: '#E2645A'
                          }
                        },
                        rules: {
                          DestinationInput: {
                            display: 'hidden',
                          },
                          ReceiptEmailInput: {
                            display: 'hidden',
                          },
                          Input: {
                            borderRadius: '4px',
                            colors: {
                              text: 'rgba(255, 255, 255, 0.8)',
                              background: 'rgba(21, 21, 23, 1)',
                              border: 'rgba(255, 255, 255, 0.2)',
                              placeholder: 'rgba(255, 255, 255, 0.4)',
                            },
                            hover: {
                              colors: {
                                  border: "rgba(255, 255, 255, 0.4)",
                              },
                            },
                            focus: {
                              colors: {
                                border: "rgba(255, 255, 255, 0.4)",
                                boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.2)",
                              },
                            },
                          },
                          PrimaryButton: {
                            borderRadius: '4px',
                            font: {
                              family: 'Audiowide, system-ui, sans-serif',
                              size: '16px',
                              weight: '400',
                            },
                            colors: {
                              background: 'rgba(26, 116, 107, 1)',
                              text: 'rgba(255, 255, 255, 0.8)',
                            },
                            hover: {
                              colors: {
                                background: 'rgba(79, 197, 186, 1)',
                                text: 'rgba(255, 255, 255, 1)',
                              },
                            },
                            disabled: {
                              colors: {
                                background: 'rgba(38, 166, 154, 0.5)',
                                text: 'rgba(255, 255, 255, 0.5)',
                              },
                            },
                          }

                        }
                      }}
                    />
                    {/* <OrderTotal total={Number(order?.quote.totalPrice?.amount) || 0} type={orderType} /> */}
                  </Flex>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </>
      )}
    </Flex>
  )
} 


export default CheckoutComponent;